import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download, Menu, X } from "lucide-react";
import { toast } from "sonner";

const links = [
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#projects" },
  { label: "Certifications", href: "#certifications" },
  { label: "Contact", href: "#contact" },
];

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const handleResume = async (e) => {
  e.preventDefault();
  try {
    const res = await fetch("/resume.pdf", { method: "HEAD" });
    const type = res.headers.get("content-type") || "";
    if (res.ok && type.includes("pdf")) {
      window.open("/resume.pdf", "_blank", "noopener,noreferrer");
    } else {
      toast.info("Resume PDF will be available here soon.");
    }
  } catch {
    toast.info("Resume PDF will be available here soon.");
  }
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(href), 250);
    } else {
      scrollToSection(href);
    }
  };

  const goRoute = (e, path) => {
    e.preventDefault();
    setOpen(false);
    navigate(path);
  };

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 z-50 w-full border-b border-white/10 bg-black/80 backdrop-blur-2xl"
    >
      <div className="flex h-16 items-center justify-between px-6 md:px-12 lg:px-20">
        <a
          href="/"
          data-testid="nav-logo"
          onClick={(e) => goRoute(e, "/")}
          className="font-mono text-sm font-bold tracking-widest text-white"
        >
          KS<span className="text-cyan-400">//</span>SAHU
        </a>
        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={(e) => go(e, l.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-cyan-400"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/ai-engineering"
            data-testid="nav-link-ai-engineering"
            onClick={(e) => goRoute(e, "/ai-engineering")}
            className={`font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-cyan-400 ${
              location.pathname === "/ai-engineering" ? "text-cyan-400" : "text-zinc-400"
            }`}
          >
            AI Engineering
          </a>
          <a
            href="/resume.pdf"
            data-testid="resume-download-btn"
            onClick={handleResume}
            className="group flex items-center gap-2 border border-cyan-400/60 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 transition-colors duration-300 hover:bg-cyan-400 hover:text-black"
          >
            Resume
            <Download className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
        </nav>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="text-white md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav data-testid="mobile-menu" className="border-t border-white/10 bg-black/95 px-6 py-6 md:hidden">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={(e) => go(e, l.href)}
                className="font-mono text-sm uppercase tracking-[0.2em] text-zinc-300"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/ai-engineering"
              data-testid="mobile-nav-link-ai-engineering"
              onClick={(e) => goRoute(e, "/ai-engineering")}
              className="font-mono text-sm uppercase tracking-[0.2em] text-cyan-400"
            >
              AI Engineering
            </a>
            <a
              href="/resume.pdf"
              data-testid="mobile-resume-download-btn"
              onClick={handleResume}
              className="flex items-center gap-2 font-mono text-sm uppercase tracking-[0.2em] text-cyan-400"
            >
              Resume <Download className="h-4 w-4" />
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};
